import React, { useEffect, useMemo, useState } from 'react'
import { useMutation } from '@apollo/client'
import { Popover, PopoverHeader, PopoverBody, Alert } from 'reactstrap'
import { RepsTypeAhead } from '../../../TaskManagement/TaskComponents/TypeAheadComponents'
import { UPDATE_COLLECTION_CASE_ASSIGNMENT } from './queries'
import './CollectionsDashboard.css'

// Map our AssignedRepNode shape ({ id (int), gid, firstName, lastName, username })
// into what RepsTypeAhead expects (`id` = Relay GID, plus name fields).
function toTypeAheadShape(rep) {
    return {
        id: rep.gid,
        firstName: rep.firstName,
        lastName: rep.lastName,
        username: rep.username,
        name: [rep.firstName, rep.lastName].filter(Boolean).join(' '),
    }
}

export default function AssignmentEditor({ caseId, target, isOpen, initialReps, onClose }) {
    // Build the typeahead's seed list ONCE per open cycle. Without this, every
    // re-render produces a fresh array reference; RepsTypeAhead's internal
    // sync effect then resets its selected list whenever the seed is empty,
    // wiping the rep the user just picked.
    const seedReps = useMemo(
        () => (isOpen ? initialReps.map(toTypeAheadShape) : []),
        // We intentionally bind to isOpen only — opening/closing the popover
        // is the natural reset boundary; re-renders while open keep the seed
        // stable so the typeahead's internal state is preserved.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isOpen],
    )

    const [selectedIds, setSelectedIds] = useState(() => initialReps.map((r) => r.gid))
    const [error, setError] = useState('')

    useEffect(() => {
        if (isOpen) {
            setSelectedIds(initialReps.map((r) => r.gid))
            setError('')
        }
        // Same rationale as seedReps — reset only when the popover opens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen])

    const [updateAssignment, { loading }] = useMutation(UPDATE_COLLECTION_CASE_ASSIGNMENT, {
        refetchQueries: ['AllCollectionCases'],
    })

    const save = () => {
        updateAssignment({
            variables: { caseId, assignedToIds: selectedIds },
        })
            .then((res) => {
                const r = res?.data?.updateCollectionCaseAssignment
                if (r && r.ok) {
                    onClose()
                } else {
                    const msg = r?.errors?.[0]?.messages?.[0] || 'Unknown error'
                    setError(msg)
                }
            })
            .catch((e) => setError(String(e)))
    }

    return (
        <Popover
            trigger="legacy"
            placement="bottom-end"
            isOpen={isOpen}
            target={target}
            toggle={onClose}
            className="cd-assignment-popover"
        >
            <PopoverHeader onClick={onClose} style={{ cursor: 'pointer' }}>
                Edit collectors
                <i style={{ float: 'right' }} className="fa fa-times-circle" aria-hidden="true" />
            </PopoverHeader>
            <PopoverBody>
                {error && <Alert color="danger">{error}</Alert>}
                <div style={{ minWidth: 280 }}>
                    <RepsTypeAhead
                        onChange={(ids) => setSelectedIds(ids || [])}
                        defaultValue={seedReps}
                        group={[]}
                        extraRepOptions={seedReps}
                    />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 12 }}>
                    <button
                        type="button"
                        className="cd-btn outline"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="cd-btn primary"
                        onClick={save}
                        disabled={loading}
                    >
                        {loading ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </PopoverBody>
        </Popover>
    )
}
